/**
 * CalendarScopeDropdown — Toolbar dropdown that scopes the calendar view to a
 * single connected Google account, or "All Calendars" for the union of every
 * account currently enabled for this Events module. Mirrors the Gmail
 * MailboxDropdown pattern so the calendar surface adopts the same mental
 * model as the inbox surface (#2726).
 *
 * Layered ON TOP of the per-account `enabledForModule` toggle:
 *   - The dropdown only ever sees accounts the toggle layer permits.
 *   - The selector cannot widen beyond the toggle's enabled set.
 *   - When a previously-selected account flips off (toggled off, disconnected,
 *     not enabled), the parent auto-resets the scope to 'all'.
 *
 * The per-calendar checkboxes inside the gear menu still work normally —
 * this dropdown gives a quick "show me only this account's events" toggle
 * without forcing the user to uncheck every other account's calendars by
 * hand.
 */

import { useEffect, useRef, useState } from 'react';

interface ScopeAccount {
  id: string;
  email: string;
}

export interface CalendarScopeDropdownProps {
  selectedScope: 'all' | string;
  onSelect: (selection: 'all' | string) => void;
  /** Pre-filtered list — disabled accounts MUST be excluded by the caller. */
  accounts: ScopeAccount[];
}

const ALL_LABEL = 'All Calendars';

export function CalendarScopeDropdown({ selectedScope, onSelect, accounts }: CalendarScopeDropdownProps) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const selectedLabel = (() => {
    if (selectedScope === 'all') return ALL_LABEL;
    const acct = accounts.find(a => a.id === selectedScope);
    return acct?.email ?? ALL_LABEL;
  })();

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close on Escape; restore focus to trigger
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  const handlePick = (selection: 'all' | string) => {
    onSelect(selection);
    setOpen(false);
  };

  // Filter out the currently-selected option so the dropdown acts as a
  // "switch to" picker — same pattern as MailboxDropdown.
  const menuOptions: Array<{ id: 'all' | string; label: string }> = [
    ...(selectedScope === 'all' ? [] : [{ id: 'all' as const, label: ALL_LABEL }]),
    ...accounts
      .filter(a => a.id !== selectedScope)
      .map(a => ({ id: a.id, label: a.email })),
  ];

  const rowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    width: '100%',
    padding: '7px 12px',
    border: 'none',
    background: 'none',
    textAlign: 'left',
    cursor: 'pointer',
    fontSize: '14px',
    color: 'var(--color-text-muted)',
    fontWeight: 500,
    whiteSpace: 'nowrap',
  };

  return (
    <div ref={wrapperRef} style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
      {/* Trigger reuses the Day/Week/Month tab look (module-toolbar-button-group +
          aria-pressed) so it follows every theme automatically; no inline fills. */}
      <div className="module-toolbar-button-group">
        <button
          ref={buttonRef}
          type="button"
          className="module-toolbar-button"
          aria-pressed={true}
          onClick={() => setOpen(v => !v)}
          title={selectedLabel}
          aria-haspopup="listbox"
          aria-expanded={open}
          style={{
            width: 150,
            overflow: 'hidden',
            justifyContent: 'flex-start',
            gap: 4,
            paddingRight: 6,
            minWidth: 0,
          }}
        >
          <span style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: '0 1 auto',
            minWidth: 0,
          }}>
            {selectedLabel}
          </span>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style={{ flexShrink: 0 }}>
            <path d="M7 10l5 5 5-5z" />
          </svg>
        </button>
      </div>
      {open && menuOptions.length > 0 && (
        <div
          role="listbox"
          aria-label="Calendar scope"
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: 4,
            zIndex: 10000,
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: 6,
            boxShadow: 'var(--popover-shadow, 0 4px 12px rgba(0,0,0,0.15))',
            padding: '2px 0',
            width: 150,
          }}
        >
          {menuOptions.map(opt => (
            <button
              key={opt.id}
              type="button"
              role="option"
              aria-selected={false}
              onClick={() => handlePick(opt.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handlePick(opt.id);
                }
              }}
              style={rowStyle}
              onMouseEnter={e => { e.currentTarget.style.background = 'var(--color-surface-hover)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
            >
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', display: 'inline-block', maxWidth: 150 }}>
                {opt.label}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
